// A building floor as a printable SVG plan, for the sign-placement guide that
// leads a Building Directories print run.
//
// The website's snapshot stores structure polygons in lat/lng, which would put
// the building on the page at its campus bearing. planFrame.js recovers the
// original floor-plan frame by fitting the transform out of the nav nodes (which
// carry both coordinate systems); everything here is drawn in that frame.
// Measured over the whole snapshot: 94.8% of wall length runs within 5 degrees
// of an axis in the recovered frame, against 0.4% in raw lat/lng.
//
// SVG rather than a rasterised canvas because a scaled PNG prints fuzzy, and a
// fuzzy plan is one somebody has to guess at while standing in a corridor
// holding a stack of sheets.
//
// Signs are labelled ON the plan with their own identifier, sitting on the node
// the sign goes at. The earlier version drew numbered discs keyed to a legend,
// on the reasoning that a dozen eight-character codes on one floor would collide
// — which they can. The label is drawn to survive that as well as it can: an
// opaque pill with a white halo, over a dot that marks the exact spot, so an
// overlap hides a neighbour's pill rather than turning both into mush, and the
// dots underneath still say how many signs are there. Reading the code off the
// plan beats carrying a number back to a legend while standing in a corridor.
// Explicit extension so this module also loads under plain node — the plan it
// draws ends up on paper, and being able to render one and look at it before a
// print run is worth more than matching the extensionless house style.
import { fitPlanFrame } from './planFrame.js';

/** Print-friendly fills: light enough that the sign markers carry the page. */
const POLY = {
  outline:  { fill: '#ffffff', stroke: '#8a8a8a', w: 1.6 },
  room:     { fill: '#f5f2ea', stroke: '#d6d6d6', w: 0.7 },
  stair:    { fill: '#e8d6c3', stroke: '#bb9269', w: 0.7 },
  elevator: { fill: '#dde3e6', stroke: '#9aabb3', w: 0.7 },
  blocked:  { fill: '#cfcfcf', stroke: '#a8a8a8', w: 0.7 },
  tunnel:   { fill: '#efebe9', stroke: '#8d6e63', w: 1.2 },
};
const MARK = '#c2185b';   // the sign markers, and the only colour on the page

const esc = s => String(s == null ? '' : s).replace(/[<>&"]/g, ch => (
  { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[ch]));

/**
 * One floor's geometry in the recovered plan frame.
 *
 * @param {object} nav        nav_<slug>.json  ({ nodes, edges })
 * @param {object|null} struct  <slug>.json    ({ polygons })
 * @param {number} floor
 * @returns {null | { rooms, nodes, aspect }}
 */
export function planFloor(nav, struct, floor) {
  const nodes = (nav?.nodes || []).filter(n => (n.floor ?? 1) === floor);
  // The frame is fitted per FLOOR, not per building: each floor was aligned
  // separately in the authoring tool (floorOffsets), so one building-wide
  // transform would leave upper floors sliding off their own footprint.
  const fit = fitPlanFrame(nodes) || fitPlanFrame(nav?.nodes || []);
  if (!fit) return null;

  const rooms = (struct?.polygons || [])
    .filter(p => p.floor === floor && Array.isArray(p.coordinates) && p.coordinates.length >= 3)
    .map(p => ({
      type: p.type || 'room',
      roomNumber: p.room_number || '',
      ring: p.coordinates.map(c => fit.project({ lat: c.latitude, lng: c.longitude })),
    }));

  const placed = nodes
    .filter(n => Number.isFinite(n.x) && Number.isFinite(n.y))
    .map(n => ({ ...n, px: n.x, py: n.y }));

  const pts = [...rooms.flatMap(r => r.ring), ...placed.map(n => ({ x: n.px, y: n.py }))];
  if (!pts.length) return null;
  const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
  const w = Math.max(...xs) - Math.min(...xs);
  const h = Math.max(...ys) - Math.min(...ys);
  return { rooms, nodes: placed, aspect: h > 1e-6 ? w / h : 1, bounds: { xs, ys } };
}

/**
 * Fit a plan's extent into a w x h box. Fitted, never cropped: a plan may come
 * out small, but it can never come out cut off — which is the one failure that
 * would make the guide worse than no guide.
 */
function fitBox(plan, w, h, pad = 10) {
  const { xs, ys } = plan.bounds;
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  const y0 = Math.min(...ys), y1 = Math.max(...ys);
  const s = Math.min((w - pad * 2) / Math.max(1e-6, x1 - x0),
                     (h - pad * 2) / Math.max(1e-6, y1 - y0));
  const ox = pad + ((w - pad * 2) - (x1 - x0) * s) / 2 - x0 * s;
  const oy = pad + ((h - pad * 2) - (y1 - y0) * s) / 2 - y0 * s;
  return { px: p => p.x * s + ox, py: p => p.y * s + oy };
}

/**
 * A floor plan as an SVG string.
 *
 * @param {object} plan   from planFloor()
 * @param {object} opts
 * @param {number} opts.w viewBox width  (unitless; the page sizes it in mm)
 * @param {number} opts.h viewBox height
 * @param {Array}  opts.marks [{ nodeId, code, label }] — the signs on this floor.
 *                 `label` overrides `code` as what is drawn on the plan: the
 *                 sheet puts the identifier there, the on-screen preview puts
 *                 the number its own legend is keyed by.
 */
export function planSvg(plan, { w = 900, h = 620, marks = [], roomNumbers = true } = {}) {
  const head = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="100%" height="100%" preserveAspectRatio="xMidYMid meet">`;
  if (!plan) return `${head}</svg>`;
  const { px, py } = fitBox(plan, w, h);
  const out = [head, `<rect width="${w}" height="${h}" fill="#fff"/>`];

  // Outlines first, so every room sits on the footprint rather than beside it.
  const rooms = [...plan.rooms].sort(
    (a, b) => (a.type === 'outline' ? -1 : 0) - (b.type === 'outline' ? -1 : 0));
  for (const r of rooms) {
    const st = POLY[r.type] || POLY.room;
    const d = r.ring.map((p, i) => `${i ? 'L' : 'M'}${px(p).toFixed(1)} ${py(p).toFixed(1)}`).join('') + 'Z';
    out.push(`<path d="${d}" fill="${st.fill}" stroke="${st.stroke}" stroke-width="${st.w}"/>`);
  }

  if (roomNumbers) {
    // Black. These are printed, often on a tired office laser, and a grey
    // 7px numeral is the first thing such a printer throws away — which is
    // exactly the text somebody is squinting at to find the room.
    out.push('<g fill="#000000" font-family="Helvetica,Arial,sans-serif" font-size="7" text-anchor="middle" dominant-baseline="middle">');
    for (const r of plan.rooms) {
      if (!r.roomNumber || r.type === 'outline' || r.type === 'blocked') continue;
      const xs = r.ring.map(px), ys = r.ring.map(py);
      // Only where the room is big enough to hold the text without overlapping
      // its own walls — a number spilling over a wall is worse than no number.
      if (Math.max(...xs) - Math.min(...xs) < 26 || Math.max(...ys) - Math.min(...ys) < 11) continue;
      const cx = xs.reduce((a, b) => a + b, 0) / xs.length;
      const cy = ys.reduce((a, b) => a + b, 0) / ys.length;
      out.push(`<text x="${cx.toFixed(1)}" y="${cy.toFixed(1)}">${esc(r.roomNumber)}</text>`);
    }
    out.push('</g>');
  }

  // The signs: the identifier itself, on the node it goes at.
  //
  // Drawn in two passes so a pill can never be half-covered by the dot of the
  // sign next to it — every dot goes down first, then every label on top.
  const byId = new Map(plan.nodes.map(n => [n.id, n]));
  const placedMarks = marks
    .map(m => ({ ...m, n: byId.get(m.nodeId) }))
    .filter(m => m.n)
    .map(m => ({ ...m, x: px({ x: m.n.px }), y: py({ y: m.n.py }) }));

  for (const m of placedMarks) {
    out.push(
      `<circle cx="${m.x.toFixed(1)}" cy="${m.y.toFixed(1)}" r="8" fill="#fff" opacity="0.95"/>`,
      `<circle cx="${m.x.toFixed(1)}" cy="${m.y.toFixed(1)}" r="5.2" fill="${MARK}"/>`);
  }

  const FS = 8.5;
  for (const m of placedMarks) {
    const text = String(m.label ?? m.code ?? '');
    if (!text) continue;
    // Bold Helvetica runs about 0.62em per character across this alphabet
    // (A-Z, 0-9, hyphen), which is close enough at this size — the pill only has
    // to clear the glyphs, and a millimetre of slack is invisible on paper.
    const hPill = FS + 5;
    // Never narrower than it is tall: a one-character label in a pill sized by
    // its text is a lozenge, and a row of them reads as different marks rather
    // than the same mark with different numbers in it.
    const wPill = Math.max(hPill, text.length * FS * 0.62 + 7);
    // Kept inside the page. A node sitting on the very edge of the plan has half
    // its pill hanging past the viewBox, and it is silently clipped — measured
    // over the whole snapshot that is 1 pill in 1,160, which is exactly the sort
    // of rare failure nobody catches before a print run. The dot underneath is
    // still at the true position, so a nudged label never lies about where the
    // sign goes.
    const x = Math.max(1, Math.min(w - wPill - 1, m.x - wPill / 2));
    const y = Math.max(1, Math.min(h - hPill - 1, m.y - hPill / 2));
    out.push(
      `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${wPill.toFixed(1)}" height="${hPill.toFixed(1)}"` +
        ` rx="${(hPill / 2).toFixed(1)}" fill="#fff" stroke="#fff" stroke-width="2.4"/>`,
      `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${wPill.toFixed(1)}" height="${hPill.toFixed(1)}"` +
        ` rx="${(hPill / 2).toFixed(1)}" fill="${MARK}"/>`,
      `<text x="${(x + wPill / 2).toFixed(1)}" y="${(y + hPill / 2 + 0.4).toFixed(1)}" fill="#fff"` +
        ` font-family="Helvetica,Arial,sans-serif" font-size="${FS}" font-weight="700"` +
        ` letter-spacing="0.3" text-anchor="middle" dominant-baseline="middle">${esc(text)}</text>`);
  }

  out.push('</svg>');
  return out.join('');
}
